import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AllUserGroupsForUserManagementDocument,
  AllUsersForAddUserDialogDocument,
  FilterForUsers,
  UserGroup,
  UsersByUserGroupDocument,
  UsersCountDocument,
  useAddUsersToUserGroupMutation,
  useAllUserGroupsForUserManagementQuery,
  useDeleteUserGroupMutation,
} from '@/__generated__/graphql';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { NavigationListItem, TabLabel } from '@/types';
import { titleCaseSentence } from '@/utils';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetAtom } from 'jotai';
import { AddPlatformDialog } from '../Dialogs/AddPlatformDialog';
import { AddUserDialog } from '../Dialogs/AddUserDialog';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { GroupDetail } from './GroupDetail';
import { NavigationPanel } from '../shared/NavigationPanel';
import { ActionButton, PanelBox, PanelTitle } from '../styles';
import { userManagementSearchPlaceholder } from '../state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { PageLoader } from '@/components/shared/PageLoader';

const searchPlaceholders = {
  [TabLabel.MEMBER_TAB]: 'Search Users',
  [TabLabel.PLATFORM_TAB]: 'Search Platforms',
};

export const GroupsPanel = () => {
  const { user } = useAuth0();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [addUserModal, setAddUserModal] = useState(false);
  const [addPlatformModal, setAddPlatformModal] = useState(false);
  const [deleteGroupModal, setDeleteGroupModal] = useState<boolean>(false);
  const [allowDeleteGroup, setAllowDeleteGroup] = useState<boolean>(false);

  const { loading, data } = useAllUserGroupsForUserManagementQuery();
  const { leftDrawerWidth } = useDrawerWidths();

  const [selectedGroupID, setSelectedGroupID] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [innerTab, setInnerTab] = useState(TabLabel.MEMBER_TAB);

  const setSearchPlaceholder = useSetAtom(userManagementSearchPlaceholder);

  useEffect(() => {
    setSearchPlaceholder(searchPlaceholders[innerTab]);
  }, [innerTab, setSearchPlaceholder]);

  const queryVariables: FilterForUsers = {
    excludeUserGroupId: selectedGroupID,
    connection: undefined,
    roleId: undefined,
    q: undefined,
  };

  const [checked, setChecked] = useState<string[]>([]);

  const handleToggle = useCallback(() => {
    if (addUserModal) setChecked([]);
    setAddUserModal(!addUserModal);
  }, [addUserModal]);

  const handleChecked = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const [addUsersToUserGroupMutation] = useAddUsersToUserGroupMutation();
  const handleAddUsers = useCallback(() => {
    if (checked && selectedGroupID) {
      addUsersToUserGroupMutation({
        variables: {
          input: {
            userGroupId: selectedGroupID,
            userIds: checked,
          },
        },
        refetchQueries: [
          UsersByUserGroupDocument,
          UsersCountDocument,
          AllUsersForAddUserDialogDocument,
        ],
        onCompleted: (response) => {
          if (response) {
            handleToggle();
            showSnackBar(`User has been added to ${selectedGroupName} successfully`, 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(
            `Failed to add user to ${selectedGroupName} user group: ${error.message}`,
            {
              user: { sub: user?.sub },
              extra: {
                userGroupId: selectedGroupID,
                userGroupName: selectedGroupName,
                userIds: JSON.stringify(checked),
              },
            }
          );
          handleToggle();
          showSnackBar(
            `Failed to add user to ${selectedGroupName} user group: ${error.message}`,
            'error'
          );
        },
      });
    }
  }, [
    addUsersToUserGroupMutation,
    checked,
    handleToggle,
    selectedGroupID,
    selectedGroupName,
    showSnackBar,
    user?.sub,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setInnerTab(newValue);
  };

  useEffect(() => {
    if (selectedGroupID === '' && !loading && data?.allUserGroups[0]) {
      setSelectedGroupID(data?.allUserGroups[0].id);
      setSelectedGroupName(data?.allUserGroups[0].name);
    }
  }, [data, loading, selectedGroupID]);

  const handleGroupClick = (groupID: string, groupName: string) => {
    setSelectedGroupID(groupID);
    setSelectedGroupName(groupName);
  };

  const handleToggleAddUserModal = () => {
    setAddUserModal(!addUserModal);
  };

  const handleToggleAddPlatformModal = () => {
    setAddPlatformModal(!addPlatformModal);
  };

  const handleDeleteGroupConfirmationToggle = useCallback(() => {
    setDeleteGroupModal(!deleteGroupModal);
  }, [deleteGroupModal]);

  const handleAllowGroupDelete = (value: boolean) => {
    setAllowDeleteGroup(value);
  };

  const [deleteGroupMutation] = useDeleteUserGroupMutation();
  const handleDeleteGroup = useCallback(() => {
    if (allowDeleteGroup) {
      deleteGroupMutation({
        variables: {
          input: {
            userGroupId: selectedGroupID,
          },
        },
        refetchQueries: [AllUserGroupsForUserManagementDocument],
        onCompleted: (response) => {
          if (response) {
            handleDeleteGroupConfirmationToggle();
            setSelectedGroupID('');
            showSnackBar('User group has been deleted successfully', 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to delete ${selectedGroupName} group ${error.message}`, {
            user: { sub: user?.sub },
            extra: {
              userGroupId: selectedGroupID,
              userGroupName: selectedGroupName,
            },
          });
          showSnackBar(`Failed to delete ${selectedGroupName} group: ${error.message}`, 'error');
        },
      });
    }
  }, [
    allowDeleteGroup,
    deleteGroupMutation,
    selectedGroupID,
    handleDeleteGroupConfirmationToggle,
    showSnackBar,
    selectedGroupName,
    user?.sub,
  ]);

  const items = useMemo(() => {
    return (data?.allUserGroups ?? [])
      .map((group: UserGroup) => {
        if (group) {
          const item: NavigationListItem = {
            id: group.id,
            value: group.name,
            label: titleCaseSentence(group.name),
            count: group.userIds.length,
            handleClick: () => {
              handleGroupClick(group.id, group.name);
            },
          };
          return item;
        }
        return undefined;
      })
      .filter((group): group is NavigationListItem => !!group);
  }, [data?.allUserGroups]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '107px' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Stack direction="row" height="100%">
      <NavigationPanel itemType="Group" items={items} selectedItemId={selectedGroupID ?? ''} />
      <PanelBox width={`calc(100vw - ${leftDrawerWidth}px)`}>
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <PanelTitle data-testid="group-title">{selectedGroupName}</PanelTitle>
            {innerTab === TabLabel.MEMBER_TAB ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <ActionButton
                  variant="text"
                  size="medium"
                  startIcon={<AddCircleRoundedIcon />}
                  onClick={handleToggleAddUserModal}
                  data-testid="add-user-button"
                >
                  Add User
                </ActionButton>
                <ActionButton
                  variant="text"
                  size="medium"
                  disabled={!allowDeleteGroup}
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={handleDeleteGroupConfirmationToggle}
                  data-testid="delete-group-button"
                >
                  Delete Group
                </ActionButton>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" spacing={2}>
                <ActionButton
                  variant="text"
                  size="medium"
                  startIcon={<AddCircleRoundedIcon />}
                  onClick={handleToggleAddPlatformModal}
                >
                  Add platform
                </ActionButton>
              </Stack>
            )}
          </Stack>

          <GroupDetail
            selectedGroupID={selectedGroupID}
            handleTabChange={handleTabChange}
            innerTab={innerTab}
            handleAllowGroupDelete={handleAllowGroupDelete}
          />

          {addPlatformModal && (
            <AddPlatformDialog
              open={addPlatformModal}
              selectedGroupID={selectedGroupID}
              handleToggle={handleToggleAddPlatformModal}
              groupName={selectedGroupName}
            />
          )}

          {addUserModal && (
            <AddUserDialog
              open={addUserModal}
              handleCheckedUsers={handleChecked}
              handleAddUsers={handleAddUsers}
              handleToggle={handleToggleAddUserModal}
              checked={checked}
              queryVariable={queryVariables}
            />
          )}

          {deleteGroupModal && (
            <ConfirmationDialog
              open={!!deleteGroupModal}
              title="Removing group"
              message={`Are you sure you want to remove ${selectedGroupName} group?`}
              buttonText="Delete Group"
              handleRemoveUserFromGroup={handleDeleteGroup}
              onClose={handleDeleteGroupConfirmationToggle}
            />
          )}
        </Box>
      </PanelBox>
    </Stack>
  );
};
